import { price } from "@atc/domain";

/**
 * What the ticker is showing, folded from the frames that arrive.
 *
 * A pure module for the same reason `marking.ts` is one: the shell holds the
 * socket and the timers, and the arithmetic that decides what a row *says* is
 * testable without either. Both setups render what this returns; neither
 * compares two prices of its own.
 *
 * **Two channels, two meanings.** The arrow is the last real price move. The
 * colour is whether the venue is open. Before M4/I6 they were one channel and
 * it said neither: direction was re-derived on every frame against the previous
 * *frame's* price, and `MarketDataHubDO` pushes a frame every second whether or
 * not the price moved — so between two prints the row went neutral for every
 * intervening second, and while the market was shut it stayed neutral forever.
 * Grey was the commonest state on a moving market and the permanent state on a
 * stopped one, which is the same as carrying no information at all.
 */

/**
 * Which way a symbol has gone TODAY, against the previous session's close.
 *
 * Not the last tick-to-tick move, which is what M4/I6 shipped and M4/I9
 * replaced. Both are real conventions and they answer different questions: an
 * uptick/downtick arrow belongs on a time-and-sales tape or a depth window,
 * where you are watching which side crossed last. On a row showing one price
 * per symbol, a reader expects the day — that is what every ticker they have
 * ever read means by a green arrow.
 *
 * `""` is "no direction to show", and it is a real answer: a symbol with no
 * previous close has nothing to be measured against, and a made-up reference
 * would be worse than an absent one.
 */
export type Direction = "up" | "down" | "";

export type TickerQuote = {
  symbol: string;
  last: string;
  /**
   * The venue's state when this price was TAKEN — the quote's own word for it,
   * never a clock the browser read. `open`, `closed` or `unknown`.
   */
  state: string;
  /**
   * The previous session's close, straight off the quote, when there is one.
   *
   * **The terminal cannot derive this and must not try.** The first price a
   * browser sees is the first price THAT browser saw, so two people watching
   * the same symbol at the same instant would disagree about which way it had
   * gone — which is PRODUCT.md's rule with a concrete failure attached.
   */
  previousClose?: string;
  direction: Direction;
};

/** A quote as it arrives, from the socket, the guest poll, or a replay frame. */
export type IncomingQuote = {
  symbol: string;
  last: string;
  market_state?: string;
  previous_close?: string;
};

/**
 * One arriving quote folded onto the one already held.
 *
 * The comparison is on scaled integers, never on the decimal strings: two
 * spellings of the same price (`252.61` and `252.610000`) must not read as a
 * move, and nothing in this file should be the place a float creeps into a
 * codebase that is integer arithmetic everywhere else.
 *
 * A missing `market_state` keeps whatever was known rather than inventing
 * `unknown` — an arriving frame that omits the field is a frame that has not
 * changed the venue's state, and downgrading to `unknown` on it would grey the
 * row on the strength of an absent field.
 */
function fold(prior: TickerQuote | undefined, q: IncomingQuote): TickerQuote {
  const state = q.market_state ?? prior?.state ?? "unknown";
  // Kept when a frame omits it, for the same reason the state is: an absent
  // field is a frame with nothing new to say, not a reference that vanished.
  const previousClose = q.previous_close ?? prior?.previousClose;

  return {
    symbol: q.symbol,
    last: q.last,
    state,
    ...(previousClose === undefined ? {} : { previousClose }),
    direction: directionOf(q.last, previousClose),
  };
}

/**
 * Up or down on the day — or neither, when there is nothing to measure against.
 *
 * Compared as scaled integers, never as decimal strings: `252.61` and
 * `252.610000` are the same price, and a provider that starts padding its
 * decimals must not read as a market that started moving. Nothing here is on
 * the money path, but two ways of reading a price in one codebase is how the
 * money path acquires a float later.
 */
export function directionOf(last: string, previousClose: string | undefined): Direction {
  if (previousClose === undefined) return "";
  const now = price(last);
  const then = price(previousClose);
  if (now === then) return "";
  return now > then ? "up" : "down";
}

/**
 * How far the day has moved, as a percentage, or undefined with no reference.
 *
 * Returned as a number because it is a ratio for display, not money — the
 * scaled-integer rule exists for values that are added to a ledger, and a
 * percentage shown to two decimal places is not one of those. The division
 * itself is done on the integers so the inputs never become floats.
 */
export function changePercent(q: TickerQuote): number | undefined {
  if (q.previousClose === undefined) return undefined;
  const then = price(q.previousClose);
  if (then === 0n) return undefined;
  const now = price(q.last);
  // Basis points as an integer first, then one division — so the only float in
  // the calculation is the last step, and it is a display value.
  const bps = ((now - then) * 10000n) / then;
  return Number(bps) / 100;
}

/**
 * Fold arriving quotes in, leaving symbols nobody mentioned untouched.
 *
 * What the live socket and the guest poll both do. A tick frame carries only
 * the symbols that have something to say, so anything absent is unchanged
 * rather than gone.
 */
export function mergeTicks(
  previous: readonly TickerQuote[],
  incoming: readonly IncomingQuote[],
): TickerQuote[] {
  const merged = new Map(previous.map((q) => [q.symbol, q]));
  for (const q of incoming) merged.set(q.symbol, fold(merged.get(q.symbol), q));
  return [...merged.values()];
}

/**
 * Show exactly these quotes, keeping the direction of any that were already up.
 *
 * What a replay frame does: a recorded day has its own symbol list, and merging
 * would leave live symbols stranded on the ticker beside it.
 */
export function replaceTicks(
  previous: readonly TickerQuote[],
  incoming: readonly IncomingQuote[],
): TickerQuote[] {
  const before = new Map(previous.map((q) => [q.symbol, q]));
  return incoming.map((q) => fold(before.get(q.symbol), q));
}

/**
 * Whether a row should read as a live price or as a stopped one.
 *
 * `unknown` reads as not-open deliberately, and the two are not the same claim:
 * `sessionAt` answers `unknown` past its calendar horizon, so the honest
 * rendering is "this is not a price you can trade against right now" rather
 * than a green arrow the market cannot back up. See M4/I4, where `unknown` is
 * carefully NOT treated as closed on the money path — refusing to trade there
 * would stop trading for no reason, while colouring a row cautiously costs
 * nothing.
 */
export const isOpen = (q: TickerQuote): boolean => q.state === "open";

/**
 * Keep only these symbols, in the order the ticker already had them.
 *
 * `unwatch` used to remove a symbol from the socket's subscription and leave
 * the quote on screen — the server stopped sending it, nothing pruned what was
 * already held, and the last price it had sat in the banner indefinitely,
 * indistinguishable from a live one (M4/E15). The subscription and the display
 * are now the same list, so there is nowhere for an orphan to survive.
 */
export function keepOnly(
  quotes: readonly TickerQuote[],
  symbols: readonly string[],
): TickerQuote[] {
  const keep = new Set(symbols.map((s) => s.toUpperCase()));
  return quotes.filter((q) => keep.has(q.symbol.toUpperCase()));
}
